import {
  DraftEmailType,
  EmailProvider,
  EmailRequestType,
} from '@this+that/models';
import log from 'loglevel';

import type { DraftAccount } from './resolveEmailAccount';
import { isDraftNotFoundError } from './useDeleteDraft';
import { DraftProbeStatus } from '../../../enums';
import {
  DraftVersionConflictError,
  type EmailSendRequest,
  sendEmailsWithoutStoreSync,
} from '../../../services/unifiedEmailAction.service';
import { DRAFT_VERSION_PROBE } from '../../../utils/constants/drafts';

export type DraftProbeResult =
  | { status: DraftProbeStatus.EXISTS; draftVersion: string }
  | { status: DraftProbeStatus.NOT_FOUND }
  | { status: DraftProbeStatus.UNKNOWN };

export interface ProbeDraftVersionParams {
  draftId: string;
  account: DraftAccount | null | undefined;
  draftType?: DraftEmailType;
}

const UNKNOWN_RESULT: DraftProbeResult = { status: DraftProbeStatus.UNKNOWN };

const buildProbeRequest = ({
  draftId,
  account,
  draftType = DraftEmailType.CREATE,
}: ProbeDraftVersionParams & { account: DraftAccount }): EmailSendRequest => ({
  providerAccount: {
    provider: account.platform as EmailProvider,
    accountId: account.accountId,
  },
  reqType: EmailRequestType.DRAFT,
  draftId,
  draftType,
  expectedDraftVersion: DRAFT_VERSION_PROBE,
});

const classifyProbeError = (error: unknown): DraftProbeResult => {
  if (error instanceof DraftVersionConflictError) {
    return error.currentDraftVersion
      ? {
          status: DraftProbeStatus.EXISTS,
          draftVersion: error.currentDraftVersion,
        }
      : UNKNOWN_RESULT;
  }
  return isDraftNotFoundError(error)
    ? { status: DraftProbeStatus.NOT_FOUND }
    : UNKNOWN_RESULT;
};

export const probeDraftVersion = async (
  params: ProbeDraftVersionParams,
): Promise<DraftProbeResult> => {
  const { account } = params;
  if (!account) {
    return UNKNOWN_RESULT;
  }

  try {
    await sendEmailsWithoutStoreSync(buildProbeRequest({ ...params, account }));
    log.warn('[Draft] version probe succeeded; the backend may have written', {
      draftId: params.draftId,
    });
    return UNKNOWN_RESULT;
  } catch (error) {
    return classifyProbeError(error);
  }
};
